/**
 * The card that tells a Creator there is something to approve.
 *
 * It appears on every page rather than on a dashboard, because the landing page here is a chat
 * REPL — someone who signs in to ask an agent a question never passes a dashboard, and a review
 * queue nobody walks past is a queue that fills up.
 *
 * Dismissal lasts for the browser session only. A reviewer who is mid-task should be able to
 * wave it away; a reviewer who signs in tomorrow should be told again.
 *
 * It names the skills rather than only counting them. The first version stated a number and put a
 * "Review skills" link under it, and the report back from RnD was that the card could not be
 * clicked to reach the skill it was about — which was accurate: a reviewer had to translate "1
 * skill" into a filtered list and then find the row. Each waiting skill is now its own link.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X, ChevronRight } from 'lucide-react';
import { usePendingReviews } from '../../hooks/usePendingReviews';
import { describePending, reviewSkillsHref } from '../../services/pendingReviews';
import { authorLabel, authorTitle } from '../../services/skillAuthor';

const DISMISS_KEY = 'review-alert-dismissed';

/** Beyond this the card becomes a list; the link to the full queue is the better answer. */
const NAMED_LIMIT = 3;

export default function ReviewAlert() {
  const { skills, articles, total, pendingSkills } = usePendingReviews();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  if (total === 0 || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="mx-4 mt-4 flex items-start gap-3 rounded-lg border border-warning bg-warning-bg/20 px-4 py-3">
      <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-textPrimary">
          Waiting for your review: {describePending(skills, articles)}
        </p>
        <p className="mt-0.5 text-sm text-textSecondary">
          A skill sits unusable until someone approves it — no agent can run it in the meantime.
        </p>

        {/* The skills by name. Clicking one opens it for reading and approving — the reviewer
            never has to work out which row of a list the count referred to. */}
        {pendingSkills.length > 0 && (
          <ul className="mt-2 space-y-1">
            {pendingSkills.slice(0, NAMED_LIMIT).map((s) => (
              <li key={s.id || s.name}>
                <Link
                  to={`/skills/${encodeURIComponent(s.name)}`}
                  className="group flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{s.title || s.name}</span>
                  {s.createdBy && (
                    <span className="flex-shrink-0 text-xs text-textTertiary" title={authorTitle(s, 'created')}>
                      · {authorLabel(s, 'created')}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          {skills > NAMED_LIMIT && (
            <Link to={reviewSkillsHref(pendingSkills)} className="text-primary hover:underline">
              All {skills} skills
            </Link>
          )}
          {articles > 0 && (
            <Link to="/knowledge/review" className="text-primary hover:underline">
              Review {articles === 1 ? 'the article' : 'articles'}
            </Link>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss until next sign-in"
        title="Dismiss until next sign-in"
        className="flex-shrink-0 rounded p-1 text-textTertiary hover:bg-surface hover:text-textPrimary"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
